import * as React from 'react';
import { DetailedHTMLProps, HTMLAttributes } from 'react';
import { BaseInputProps, DropdownOption } from '@tcl-argon-models/models';
import { StatusVariant } from '@tcl-argon-utils/utils';

import { Select } from '@tcl-boron-foundations/select';

type DivElement = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface SingleSelectTypeaheadProps extends BaseInputProps, Omit<DivElement, 'onChange'> {
  /** If `true`, show an "X" button that, when clicked, will clear a user's current selection. */
  clearable?: boolean;
  /**
   * A handler called whenever the selected option changes.
   * The selected option is passed as the only argument to the callback.
   */
  onChange: (selectedItem: DropdownOption | null) => void;
  /** An array of DropdownOptions that use user can select. */
  options: DropdownOption[];
  /** A DropdownOption that represent a user's current selection. */
  value: DropdownOption | null;
  /** status either 'success' | 'error' | 'warning' | 'default' changes colors to visually reflect status of dropdown */
  status?: StatusVariant;
  /** whether the dropdown renders as primary or secondary style, the main difference being border radius */
  dropdownType?: 'primary' | 'secondary';
  /** when true, will show loading state in dropdown */
  isLoading?: boolean;
  /** handler triggered after the debounce time is reached */
  onDebounceChange?: (value: string) => void;
  /** value to override the default debounce time (1000ms) */
  debounceTimeOverride?: number;
  /** value of text in input field */
  inputValue?: string;
  /** handler triggered when the input value changes */
  onInputValueChange?: (value: string) => void;
  /** optional with of menu, cannot be less than input width */
  menuWidth?: string;
  /** when true, menu has fixed position, allowing it to extend outside of other components */
  escapeClippingArea?: boolean;
  /** subset of options that is shown when no input value is given as "suggestions" */
  defaultOptions?: DropdownOption[];
  /** override for the suggested options label. Defaults to 'Suggestions' */
  defaultOptionsLabel?: string;
}

export const SingleSelectTypeahead: React.FC<SingleSelectTypeaheadProps> = ({
  onChange,
  value,
  escapeClippingArea = false,
  defaultOptionsLabel = 'Suggestions',
  ...props
}) => {
  return (
    <Select
      {...{
        onChange: (selectedItems: DropdownOption[]) => onChange(selectedItems[0]),
        value: value === null || value === undefined ? [] : [value],
        isSingleSelect: true,
        isComboBox: true,
        isTypeahead: true,
        escapeClippingArea,
        defaultOptionsLabel,
        ...props,
      }}
    />
  );
};
